import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { api, fmtZAR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, Wallet, Ticket, ScanLine, ArrowUpRight, AlertCircle } from "lucide-react";

export default function MerchantDashboard() {
  const { user, merchant, wallet, refresh } = useAuth();
  const [vouchers, setVouchers] = useState([]);

  useEffect(() => {
    refresh();
    api.get("/vouchers").then(({ data }) => setVouchers(data.vouchers || [])).catch(() => {});
  }, [refresh]);

  const stats = {
    active: vouchers.filter((v) => v.status === "active" || v.status === "partially_redeemed").length,
    issued: vouchers.length,
    liability: vouchers
      .filter((v) => v.status === "active" || v.status === "partially_redeemed")
      .reduce((s, v) => s + (v.balance || 0), 0),
  };

  return (
    <DashboardShell role="merchant">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">Welcome</p>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold text-[#0A2540]">{user?.full_name}</h1>
          {merchant?.business_name && <p className="text-sm text-[#666666] mt-1">{merchant.business_name}</p>}
        </div>
        <Link to="/dashboard/vouchers"><Button data-testid="dashboard-issue-voucher-btn" className="bg-[#00C4B4] hover:bg-[#009E91] text-white rounded-full">Issue voucher →</Button></Link>
      </div>

      {merchant?.kyc_status !== "approved" && (
        <div data-testid="kyc-banner" className="bg-orange-50 border border-orange-200 rounded-xl p-5 mb-6 flex items-start gap-3">
          <AlertCircle className="text-orange-600 shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-[#0A2540]">KYC required to start issuing vouchers</p>
            <p className="text-sm text-[#666666] mt-1">Your KYC status is <strong className="uppercase">{merchant?.kyc_status || "pending"}</strong>. Submit your documents to get approved.</p>
            <Link to="/dashboard/kyc" data-testid="kyc-banner-cta" className="inline-block mt-3 text-sm font-semibold text-[#00C4B4] hover:underline">
              Complete KYC →
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard label="Wallet balance" value={fmtZAR(wallet?.balance || 0)} accent="#0A2540" icon={Wallet} testid="stat-wallet" />
        <StatCard label="Vouchers issued" value={stats.issued} accent="#00C4B4" icon={Ticket} testid="stat-issued" />
        <StatCard label="Active vouchers" value={stats.active} accent="#00C853" icon={ShieldCheck} testid="stat-active" />
        <StatCard label="Outstanding" value={fmtZAR(stats.liability)} accent="#FF9F1C" icon={ArrowUpRight} testid="stat-liability" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <QuickAction to="/dashboard/wallet" icon={Wallet} title="Top-up wallet" desc="Deposit and upload proof for admin approval." />
        <QuickAction to="/dashboard/vouchers" icon={Ticket} title="Generate vouchers" desc="Create 16-digit PINs in any denomination." />
        <QuickAction to="/dashboard/redeem" icon={ScanLine} title="Redeem voucher" desc="Look up a PIN and redeem partially." />
      </div>
    </DashboardShell>
  );
}

function StatCard({ label, value, accent, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">{label}</p>
        <span className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}12`, color: accent }}><Icon size={16} /></span>
      </div>
      <p className="font-display mt-3 text-2xl font-bold text-[#0A2540]">{value}</p>
    </div>
  );
}

function QuickAction({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to} data-testid={`quick-${title.toLowerCase().replace(/\s/g, "-")}`} className="bg-white border border-[#E5E7EB] rounded-2xl p-6 hover:border-[#00C4B4] hover:-translate-y-0.5 transition-all block">
      <Icon className="text-[#00C4B4]" size={22} />
      <p className="font-display mt-4 text-lg font-semibold text-[#0A2540]">{title}</p>
      <p className="text-sm text-[#666666] mt-1">{desc}</p>
    </Link>
  );
}
